import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Lesson learned, test your migrations on the big dataset",
  "description": "",
  "published": "2015-09-02T00:00:00.000Z",
  "redirect_from": ["/blog/lesson-learned-test-your-migrations-on-the-big-dataset/swizec/6729"],
  "categories": "Active record pattern, CPU time, Database, Heroku, Rails, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Data migrations:`}<br parentName="p"></br><br parentName="p"></br>{`Oh I can do this in an hour. Two tops.`}<br parentName="p"></br><br parentName="p"></br>{`Two days later: (╯°□°）╯︵ ┻━┻`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/634452345007550464"
      }}>{`August 20, 2015`}</a></blockquote>
    <p>{`On Thursday we pushed to production. Three months worth of code went from no users, to thousands of users. Or hundreds. Depends how you count.`}</p>
    <p>{`My point is, our code went through the ultimate integration test - the real world.`}</p>
    <p>{`The whole thing was much less intense than expected. We pushed in the middle of the night, ran our migrations, waited 36 minutes for them to run, ate some pizza while waiting for the fires to start, and went home when they didn't. An uneventful deploy is the best kind of deploy.`}</p>
    <p>{`Well okay, I `}<em parentName="p">{`say`}</em>{` no fires started, but that's not quite true. The whole engineering team has spent the last couple of days fixing known bugs that slipped through, squashing unknown bugs that users on strange computers in strange lands discovered by clicking something they shouldn't, manually tweaking the production database to get people out of unforeseen corner cases, and implementing some features we forgot we needed.`}</p>
    <p>{`Such is life without QA. You forget things.`}</p>
    <p>{`As David Heinemeier Hansson once said: it's not worth fixing, if a user doesn't complain about it.`}</p>
    <p>{`Right?`}</p>
    <p>{`I kid, we knew hiccups would happen. Hair-on-fire mode lasting just three days after a major deploy is good. Very good.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`That moment when workers run while you're importing data and duplicate ids and your db doesn't get primary keys and rails gets confused.`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/634859142432333824"
      }}>{`August 21, 2015`}</a></blockquote>
    <p>{`But the week before was the most frustrating week of my life. I was this close to tearing my hair out, punching my computer in the face, and taking up goat farming. This close. `}<em parentName="p">{`does the this close finger gesture`}</em></p>
    <p>{`I was doing a test run of the database migrations we accumulated in those three months. Shouldn't take more than two hours, right? Wrong. It took a week.`}</p>
    <p>{`Some of our database migrations are also data migrations. Mistake no. 1.`}</p>
    <p>{`None of our new migrations that we've had for three months have ever been run against the full database. Mistake no. 2.`}</p>
    <p>{`Our whole database is under 500,000 lines. The entire data dump falls well under 200 megabytes. Our database is `}<em parentName="p">{`tiny`}</em>{`. What could possibly go wrong?`}</p>
    <p>{`Oh so very much could.`}</p>
    <p>{`Most migrations ran in a fraction of a second. Many took a second or two. A few beasts took ten minutes.`}</p>
    <p>{`Ten whole minutes for a single migration on a tiny database. The mind recoils in horror, unable to understand what could be taking so long.`}</p>
    <p>{`When we wrote a migration, we tested it on our local machines. 2.3 gigahertz computers with six or eight CPU cores, 16 gigabytes of memory, a solid state drive, and a database that could fit on a floppy drive.`}</p>
    <p><em parentName="p">{`"It was fast when I tried it"`}</em>{`. Of course it was. Your computer is a monster running on bare metal. Heroku dynos are paltry "computers" running on many layers of virtualization. They're meant to zerg rush a problem, not handle a big thing on their own.`}</p>
    <p>{`You run into problems when your migrations look like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`  def change
    add_column :promotional_tokens, :type, :string

    Token::PromotionalToken.reset_column_information

    Token::PromotionalToken.all.find_each do |token|
      if token.promotion_type.eql? 'referral'

        # remove if user is not a student
        user = User.find(token.user_id)

        if user.is? :student #refactor it to student profile
          token.update!(type: 'Token::ReferralToken' )
          ref_token = Token::ReferralToken.find(token.id)
          user.profile.update!(referral_token: ref_token)

        else #remove it as it is now redundant
          token.destroy
        end
      end
    end
  end
`}</code></pre>
    <p>{`This migration adds a `}<inlineCode parentName="p">{`type`}</inlineCode>{` column to `}<inlineCode parentName="p">{`promotional_tokens`}</inlineCode>{`, then goes through existing tokens - of which each user has one - and updates them. Students get a new token, everybody else has theirs removed. Because that's what business wants.`}</p>
    <p>{`There are 90,000 users in the system.`}</p>
    <p>{`Let's play spot the problem.`}</p>
    <ol>
      <li parentName="ol">{`Using `}<inlineCode parentName="li">{`.all`}</inlineCode>{` that loads the whole table into memory`}</li>
      <li parentName="ol">{`We run a DB query for every token to fetch the user`}</li>
      <li parentName="ol">{`Then we run a DB query to update the token`}</li>
      <li parentName="ol">{`Then we run a DB query to get the token again, but with the correct object type`}</li>
      <li parentName="ol">{`Then we run a DB query to move the token reference to `}<inlineCode parentName="li">{`user.profile`}</inlineCode></li>
      <li parentName="ol">{`If the user isn't a student, we run a destroy query instead`}</li>
    </ol>
    <p>{`There's only 200 users who aren't students. We can ignore those.`}</p>
    <p>{`For every user we have, this migration hits the database four times. 360,000 database queries. And just to make it more fun, each is made using ActiveRecord instead of raw SQL.`}</p>
    <p>{`It took 10 minutes to run through the whole dataset on my local machine. It flat out crashed Heroku's one-off dyno. Out of memory.`}</p>
    <p>{`And if it didn't crash, who knows. Our database runs on a separate server. Maybe in the same datacenter, maybe not. Heroku knows, we don't.`}</p>
    <p>{`That makes migrations even slower.`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`That moment when your laptop is so much stronger than Heroku dynos that you decide to migrate data locally.`}</p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/634835917329571840"
      }}>{`August 21, 2015`}</a></blockquote>
    <p>{`I'm not kidding. I legit considered snapshotting the production database, running migrations locally, then swapping production with the updated snapshot. That could screw things up, especially permissions, but at least I could run it.`}</p>
    <p>{`We salvaged this migration after many struggles. Like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`  def change
    add_column :promotional_tokens, :type, :string

    Token::PromotionalToken.reset_column_information
    User.with_role(:tutor).includes(:promotional_tokens).find_each do |tutor|
      tutor.promotional_tokens.destroy_all
    end

    Token::PromotionalToken.update_all(type: 'Token::ReferralToken')

    User.with_role(:student).includes(:referral_token, :profile).find_each do |student|
      tok = student.referral_token

      student.profile.update_attribute(:referral_token, tok)
    end
  end
`}</code></pre>
    <p>{`Doesn't look like much, but an order of magnitude less queries hit the database. The magic of scoping and `}<inlineCode parentName="p">{`.includes`}</inlineCode>{`.`}</p>
    <p>{`200 queries run to delete non-student tokens. Nothing you can do about that. A single query updates the type of all tokens still in the system. And a single query per token, moves the token reference to `}<inlineCode parentName="p">{`student.profile`}</inlineCode>{`.`}</p>
    <p>{`Some 90,003 queries in total. Takes a minute or two.`}</p>
    <p>{`In school they teach you that O(4n+1) is the same as O(n+3). It's just linear complexity, who cares. Reality cares. 4 times 90,000 is a lot more than 90,000. Especially when you're talking to a database across TCP/IP.`}</p>
    <p>{`All in all it took myself and the genius who sits across from me an entire week to get our migrations running in under 20 minutes on a MacBook Pro. We removed a lot of `}<inlineCode parentName="p">{`.all`}</inlineCode>{`s, reduced many factors of linear complexity, and in one epic instance even turned linear complexity into constant complexity. That was cool.`}</p>
    <p>{`We solved the rest by using `}<inlineCode parentName="p">{`--size=px`}</inlineCode>{`. That ran our migrations with Heroku's beefiest dyno. 2.5 gigs of memory, dedicated machine, and 12x of computing. Whatever that means.`}</p>
    <p>{`It took 36 minutes to run our migrations on production.`}</p>
    <p>{`It took 36 seconds to test them in our dev environment.`}</p>
    <p>{`Lesson learned.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      